import { useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useNavigate } from 'react-router-dom';

import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  getHostingOnboardingDataQueryKey,
  useHostingOnboardingDataUpdate,
  useSiteUpdate,
} from '@newfold/huapi-js';
import { HostingOnboardingData200 } from '@newfold/huapi-js/src/index.schemas';

import useAlerts from '~/components/Alerts/alertsStore';
import { useMFEContext } from '~/components/MFEProvider';
import { canonicalizeUrl } from '~/utils/site/helpers';

import ChooseTitle from '../components/ChooseTitle';
import MultiStepCard from '../components/MultiStepCard/index';

type SetupSitePropOptions = {
  redirectSSO?: VoidFunction;
  changeLoading?: boolean;
  setChangeLoading?: (state: boolean) => void;
  comfortLevel?: number | null;
  setComfortLevel?: (state: number) => void;
};

const SetupSite = ({
  redirectSSO = () => {},
  changeLoading = false,
  setChangeLoading = () => {},
  comfortLevel = null,
  setComfortLevel = () => {},
}: SetupSitePropOptions) => {
  const { t } = useTranslation('addSites');
  const [, { generateAlert }] = useAlerts();
  const queryClient = useQueryClient();
  // TODO: remove the comment marks when survey gets implemented.
  //       Add the imports and dependencies as needed
  // const { siteId } = useParams();
  const navigate = useNavigate();
  // @ts-expect-error
  const { hostingId } = useMFEContext();

  const [formData, setFormData] = useState({
    siteName: '',
    domain: '',
    path: '',
    autoGeneration: false,
  });

  const hostingQueryKey = getHostingOnboardingDataQueryKey(hostingId);
  const { mutate: hostingOnboardingDataUpdate } =
    useHostingOnboardingDataUpdate({
      mutation: {
        onMutate: () => {
          queryClient.setQueryData<AxiosResponse<HostingOnboardingData200>>(
            hostingQueryKey,
            (oldData) => ({
              ...oldData!,
              data: {
                ...oldData?.data!,
                onboarding: { ...oldData?.data, completed: true },
              },
            }),
          );
        },
      },
    });

  const { mutate: siteUpdate } = useSiteUpdate({
    mutation: {
      onError: (error) => {
        if (error?.response?.data?.error === 'urlInUse') {
          generateAlert({
            title: t('alert.urlInUseTitle'),
            description: t('alert.urlInUseDesc', {
              path: canonicalizeUrl(`${formData.domain}/${formData.path}`),
            }),
            severity: 'error',
            showCloseBtn: true,
            persist: true,
          });
          navigate('survey');
        } else {
          generateAlert({
            description: t('setupError'),
            severity: 'error',
            showCloseBtn: true,
          });
        }
        setChangeLoading(false);
      },
      // TODO: Verify that we don't need to check the site in the site list anymore before calling sso
      onSuccess: () => redirectSSO(),
    },
  });

  // TODO: remove the comment marks when survey gets implemented.
  //       Add the imports and dependencies as needed
  // const handleSubmit = () => {
  //   setChangeLoading(true);
  //   hostingOnboardingDataUpdate({
  //     hostingId,
  //     data: { completed: true },
  //   });
  //   siteUpdate({
  //     siteId,
  //     data: {
  //       title: formData.siteName,
  //       domain: formData.domain,
  //       path: formData.path,
  //     },
  //   });
  // };

  return (
    <Container data-testid="setup-site-flow">
      <Stack spacing={3}>
        <Typography variant="h1" color="primary">
          {t('setupSite')}
        </Typography>
        <MultiStepCard>
          <Routes>
            <Route
              index
              element={
                <ChooseTitle
                  formData={formData}
                  setFormData={setFormData}
                  changeLoading={changeLoading}
                  setChangeLoading={setChangeLoading}
                  hostingOnboardingDataUpdate={hostingOnboardingDataUpdate}
                  siteUpdate={siteUpdate}
                />
              }
            />
            {/* TODO: remove the comment marks when survey gets implemented. 
                Add the imports and dependencies as needed
            <Route path="survey/*">
              <Route
                index
                element={
                  <MoleSurvey
                    changeLoading={changeLoading}
                    handleSubmit={handleSubmit}
                    comfortLevel={comfortLevel}
                    setComfortLevel={setComfortLevel}
                  />
                }
              />
              <Route
                path="contact"
                element={
                  <ContactInfo
                    changeLoading={changeLoading}
                    handleSubmit={handleSubmit}
                  />
                }
              />
            </Route> */}
          </Routes>
        </MultiStepCard>
      </Stack>
    </Container>
  );
};

export default SetupSite;
